/* eslint-disable react-hooks/exhaustive-deps */
import { BarChart3, Plus } from "lucide-react";
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { subDays } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import cn from "../../../../../utils/styleHelpers";
import { queryClient, useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import { getCompanyMetrics } from "../../../../../services/companyEndpoint";
import { getTraitHistory } from "../../../../../services/computedTraits/traitHistoryEndpoints";
import { createMetric, deleteMetric, updateMetric } from "../../../../../services/metric/metricEndpoint";
import { updateWorkspacePreference } from "../../../../../services/preference/preferenceEndpoints";
import dateConverter from "../../../../../utils/dateConverter";
import notify from "../../../../../utils/notify";
import CustomChart from "./CustomChart";
import NewTrendModal from "./NewTrendModal";

type Props = {
  account: any;
  prefs: any;
  metrics: any;
  currentWorkspace: any;
  containerClassName?: string;
};

// ── Helpers to classify fields ──

function buildPinnableAs(f: any): string[] {
  // Event metrics → line, bar, stat
  if (f._source === "event_metric") return ["line", "bar", "stat"];
  // Computed traits with history → line, bar, stat
  if (f._source === "computed_trait" && f.track_history) return ["line", "bar", "stat"];
  // Computed traits without history → stat only
  if (f._source === "computed_trait" && f.track_history === false) return ["stat"];
  // Computed traits where track_history is unknown (null) → allow all chart types
  if (f._source === "computed_trait") return ["line", "bar", "stat"];
  // Everything else (regular traits) → value only
  return ["value"];
}

/** Ensure computed trait column_name has exactly one c__ prefix */
function computedName(f: any): string {
  const col = f.field_name || f.column_name || "";
  return col.startsWith("c__") ? col : `c__${col}`;
}

const TrendsContainer = ({
  account, currentWorkspace, prefs, metrics, containerClassName,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const updatePrefMutation: any = useMutation({
    mutationFn: updateWorkspacePreference,
    onSuccess: () => {
      queryClient.invalidateQueries(["workspace_preferences", currentWorkspace.id]);
    },
  });

  const deleteMetricMutation: any = useMutation({
    mutationFn: deleteMetric,
  });

  const updateMetricMutation: any = useMutation({
    mutationFn: updateMetric,
  });

  const firstRender = useRef(true);

  const [bucket, setBucket] = useState<any>(
    prefs?.data?.accounts?.metrics?.grouping || "day"
  );

  const [duration, setDuration] = useState<any>(
    prefs?.data?.accounts?.metrics?.time_window || "last_7_days"
  );

  useEffect(() => {
    if (prefs?.data?.accounts?.metrics) {
      const prefBucket = prefs.data.accounts.metrics.grouping;
      const prefTimeWindow = prefs.data.accounts.metrics.time_window;

      if (prefBucket && prefBucket !== bucket) {
        setBucket(prefBucket);
      }

      if (prefTimeWindow && prefTimeWindow !== duration) {
        setDuration(prefTimeWindow);
      }
    }
  }, [prefs?.data?.accounts?.metrics, bucket, duration]);

  const [tableData, setTableData] = useState<any>({});

  useEffect(() => {
    if (bucket && duration) {
      if (duration !== prefs?.data?.accounts?.metrics?.time_window && !firstRender.current) {
        updatePrefMutation.mutate({
          workspace_id: currentWorkspace.id,
          accounts: {
            ...prefs?.data?.accounts,
            metrics: {
              grouping: bucket,
              time_window: duration,
              items: [],
            },
          },
          users: prefs?.data?.users,
          updated_by: prefs?.data?.updated_by,
          id: prefs?.data?.id,
        });
      } else {
        firstRender.current = false;
      }
    }
  }, [bucket, duration]);

  const bucketList = useMemo(() => (
    [{ value: "day", label: "Daily" },
      { value: "week", label: "Weekly" },
      { value: "month", label: "Monthly" },
    ]
  ), []);

  const durationList = useMemo(() => {
    if (bucket === "day") {
      return [
        { value: "last_7_days", label: "Last 7 days" },
        { value: "last_30_days", label: "Last 30 days" },
        { value: "last_90_days", label: "Last 90 days" },
        { value: "last_180_days", label: "Last 180 days" },
      ];
    } if (bucket === "week") {
      return [
        { value: "last_4_weeks", label: "Last 4 weeks" },
        { value: "last_8_weeks", label: "Last 8 weeks" },
        { value: "last_12_weeks", label: "Last 12 weeks" },
        { value: "last_24_weeks", label: "Last 24 weeks" },
      ];
    } if (bucket === "month") {
      return [
        { value: "last_3_months", label: "Last 3 months" },
        { value: "last_6_months", label: "Last 6 months" },
        { value: "last_9_months", label: "Last 9 months" },
        { value: "last_12_months", label: "Last 12 months" },
      ];
    } return [];
  }, [bucket]);

  const onEditTitle = (id: any, title: any) => {
    updateMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
      display_name: title,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.map((item: any) => (item.id === data.data.id ? data.data : item)),
        }));
        notify({
          type: "success",
          messageList: ["Updated Pinned metric"],
        });
      },
      onError: () => {
        notify({
          type: "failure",
          messageList: ["Failed to update Pinned metric"],
        });
      },
    });
  };

  const onDeleteClick = (id: any) => {
    deleteMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.filter((item: any) => item.id !== data.data.id),
        }));
        if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "deleted_pinned_metric",
            {
              properties: {
                metric_id: id,
                workspace_id: currentWorkspace.id,
                entity_type: "accounts",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Removed Pinned metric"],
        });
      },
      onError: () => {
        notify({
          type: "failure",
          messageList: ["Failed to remove Pinned metric"],
        });
      },
    });
  };

  // ── Data fetching ──
  const getMetrics = async () => {
    setLoading(true);
    let days = 7;
    const durationMatch = duration.match(/\d+/);
    const durationNumber = durationMatch ? parseInt(durationMatch[0], 10) : 7;

    if (bucket === "day" || duration.includes("days")) {
      days = durationNumber;
    } else if (bucket === "week" || duration.includes("weeks")) {
      days = durationNumber * 7;
    } else if (bucket === "month" || duration.includes("months")) {
      days = durationNumber * 30;
    }
    try {
      const apiPromises = metrics?.map(async (event: any) => {
        // ── "value" type: read directly from account object ──
        if (event.type === "value") {
          const fieldKey = event.aggregator?.startsWith("c__")
            ? event.aggregator.slice(3)
            : event.aggregator;
          const rawValue = account?.[fieldKey] ?? account?.[event.aggregator];
          return {
            data: {
              [event.aggregator]: {
                current_value: rawValue ?? null,
                display_type: "value",
              },
            },
          };
        }

        // ── "stat" type for regular traits (no c__ prefix, not an event metric) ──
        if (event.type === "stat" && !event.aggregator?.startsWith("c__")) {
          // Check if this is a regular trait (not an event metric)
          const isEventMetric = baseAccountMetrics?.obj?.[event.aggregator];
          if (!isEventMetric) {
            const fieldKey = event.aggregator;
            const rawValue = account?.[fieldKey];
            return {
              data: {
                [event.aggregator]: {
                  total_count: rawValue ?? "N/A",
                  percentage_change: null,
                  current_value: rawValue ?? null,
                  display_type: "stat",
                },
              },
            };
          }
        }

        // ── Computed traits use trait history API (prefix c__) ──
        if (event.aggregator?.startsWith("c__")) {
          const response = await getTraitHistory({
            workspace_id: currentWorkspace?.id,
            entity_type: "accounts",
            record_id: account?.external_id,
            trait_name: event.aggregator,
            days,
          });
          const historyData = response?.data?.history || [];
          return {
            data: {
              [event.aggregator]: {
                histogram_values: historyData.map((h: any) => ({
                  date: h.snapshot_date || h.date,
                  count: h.value_numeric ?? h.value ?? h.count ?? 0,
                })),
                total_count: response?.data?.total_count ?? (historyData.length > 0 ? (historyData[0]?.value_numeric ?? historyData[0]?.value) : 0),
                percentage_change: response?.data?.percentage_change ?? null,
                current_value: response?.data?.current_value ?? (historyData.length > 0 ? (historyData[0]?.value_numeric ?? historyData[0]?.value) : null),
              },
            },
          };
        }

        // ── Event metrics ──
        const response = await getCompanyMetrics({
          bucket_size: bucket,
          external_id: account?.external_id,
          event_name: event.aggregator,
          workspace_identifier: currentWorkspace?.id,
          from_date: dateConverter(subDays(new Date(), days)),
        });
        return response;
      });
      const resolvedPromises = await Promise.all(apiPromises);
      resolvedPromises.forEach((response: any) => {
        if (response?.data) {
          setTableData((state: any) => ({ ...state, ...response.data }));
        }
      });
    } catch (error) {
      setTableData({});
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!!account?.external_id && !!currentWorkspace?.id && metrics?.length > 0) {
      getMetrics();
    }
  }, [account?.external_id, bucket, duration, currentWorkspace?.id, metrics?.length]);

  // ── Build expanded accountMetrics list ──
  const {
    data: workspaceSchema,
  }: any = useWorkspaceObjectSchema({
    workspaceId: currentWorkspace?.id,
    enabled: !!currentWorkspace?.id,
  });

  const baseAccountMetrics = workspaceSchema?.metrics
    || { list: [], obj: {} };

  const accountMetrics = useMemo<{ list: any[]; obj: Record<string, any> }>(() => {
    const accountFields: any[] = workspaceSchema?.fieldsByObjectSlug?.accounts?.list || [];

    // 1. Event metrics (real events only, exclude system defaults)
    const eventMetrics: any[] = (baseAccountMetrics?.list || [])
      .filter((m: any) => !m.name?.startsWith("default_metric_") && !m.is_system)
      .map((m: any) => ({
        ...m,
        attribute_type: "metrics" as const,
        pinnable_as: ["line", "bar", "stat"],
        _source: "event_metric",
      }));

    // 2. Computed traits WITH history → shown under "Traits" tab in picker
    const computedWithHistory: any[] = accountFields
      .filter((f: any) => f.generation_type === "computed_trait" && f.track_history)
      .map((f: any) => {
        const item: any = {
          ...f,
          name: computedName(f),
          display_name: f.display_name || f.field_name,
          attribute_type: "computed_traits" as const,
          _source: "computed_trait",
        };
        item.pinnable_as = buildPinnableAs(item);
        return item;
      });

    // 3. Computed traits WITHOUT history
    const computedWithoutHistory: any[] = accountFields
      .filter((f: any) => f.generation_type === "computed_trait" && !f.track_history)
      .map((f: any) => {
        const item: any = {
          ...f,
          name: computedName(f),
          display_name: f.display_name || f.field_name,
          attribute_type: "computed_traits" as const,
          _source: "computed_trait",
        };
        item.pinnable_as = buildPinnableAs(item);
        return item;
      });

    // 4. Regular account traits (exclude computed, exclude hidden/system)
    const regularTraits: any[] = accountFields
      .filter((f: any) =>
        f.generation_type !== "computed_trait" &&
        !f.exclude_display &&
        !f.is_hidden &&
        f.column_name !== "external_id" &&
        f.column_name !== "id",
      )
      .map((f: any) => {
        const item: any = {
          ...f,
          name: f.column_name || f.field_name,
          display_name: f.display_name || f.column_name,
          attribute_type: "traits" as const,
          _source: "trait",
        };
        item.pinnable_as = buildPinnableAs(item);
        return item;
      });

    const allItems = [...eventMetrics, ...computedWithHistory, ...computedWithoutHistory, ...regularTraits];
    const obj: Record<string, any> = {};
    allItems.forEach((m) => { obj[m.name] = m; });

    return { list: allItems, obj };
  }, [baseAccountMetrics, workspaceSchema?.fieldsByObjectSlug?.accounts?.list]);

  // ── Field definition lookup for CustomChart ──
  const fieldDefMap = useMemo(() => {
    const map: Record<string, any> = {};
    const accountFields: any[] = workspaceSchema?.fieldsByObjectSlug?.accounts?.list || [];
    accountFields.forEach((f: any) => {
      const key = f.column_name || f.field_name;
      map[key] = f;
      map[`c__${key}`] = f;
    });
    return map;
  }, [workspaceSchema?.fieldsByObjectSlug?.accounts?.list]);

  const selectedEvents = useMemo(() => metrics
    ?.map((item: any) => item.aggregator), [metrics]);

  const createMetricMutation: any = useMutation({
    mutationFn: createMetric,
  });

  const shownList = useMemo(() => (
    accountMetrics?.list?.filter((item: any) => !selectedEvents?.find(
      (event: any) => event === item.name,
    ))
  ), [selectedEvents, accountMetrics?.list]);

  const shownTraitsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "traits") || [],
    [shownList],
  );
  const shownSuccessMetricsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "computed_traits") || [],
    [shownList],
  );
  const shownMetricsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "metrics") || [],
    [shownList],
  );

  const fullMetricsList = useMemo(() => (
    accountMetrics?.list || []
  ), [accountMetrics?.list]);

  // Check if any metrics need chart time controls (line or bar)
  const hasChartMetrics = useMemo(() => (
    metrics?.some((m: any) => m.type === "line" || m.type === "bar")
  ), [metrics]);

  const onUpdateMetric = (id: any, metric: string, metricName: string, chartType: string = "line") => {
    updateMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
      display_name: metricName,
      aggregator: metric,
      type: chartType,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.map((item: any) => (item.id === data.data.id ? data.data : item)),
        }));

        if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "updated_pinned_metric",
            {
              properties: {
                display_name: metricName,
                aggregator: metric,
                workspace_id: currentWorkspace.id,
                entity_type: "accounts",
              },
            },
          );
        }

        notify({
          type: "success",
          messageList: ["Updated Pinned metric successfully"],
        });

        getMetrics();
      },
      onError: () => {
        notify({
          type: "failure",
          messageList: ["Failed to update Pinned metric"],
        });
      },
    });
  };

  const onMetricSubmit = (metric: string, metricName: string, type: string) => {
    createMetricMutation.mutate({
      workspace_id: currentWorkspace?.id,
      aggregator: metric,
      display_name: metricName,
      description: "",
      type,
      id: `am_${metric}_${Math.random() * 1000}`,
      linked_entity: "accounts",
      is_default: false,
    }, {
      onSuccess: (newMetric: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace?.id], (oldData: any) => ({
          ...oldData,
          data: [...oldData.data, newMetric.data],
        }));
        if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "created_pinned_metric",
            {
              properties: {
                display_name: metricName,
                aggregator: metric,
                workspace_id: currentWorkspace.id,
                entity_type: "accounts",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Pinned metric successfully"],
        });
        setOpened(false);
      },
      onError: () => {
        notify({
          type: "failure",
          messageList: ["Something went wrong"],
        });
      },
    });
  };

  return (
    <div className={cn("w-full bg-white border-gray-200 p-5", containerClassName)}>
      <NewTrendModal
        onMetricSubmit={onMetricSubmit}
        traitsList={shownTraitsList}
        successMetricsList={shownSuccessMetricsList}
        metricsList={shownMetricsList}
        opened={opened}
        close={() => setOpened(false)}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Pinned Metrics</h3>
        <div className="flex items-center gap-2">
          {hasChartMetrics && (
            <>
              <Select
                value={bucket}
                onValueChange={(value) => {
                  setBucket(value);
                  let dur;
                  if (value === "day") dur = "last_7_days";
                  else if (value === "week") dur = "last_4_weeks";
                  else if (value === "month") dur = "last_3_months";
                  setDuration(dur);
                }}
              >
                <SelectTrigger className="w-[100px] h-7 text-xs">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {bucketList.map((item) => (
                    <SelectItem key={item.value} value={item.value} className="text-xs">
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={duration}
                onValueChange={(value) => {
                  setDuration(value);
                  if (value !== prefs?.data?.accounts?.metrics?.time_window) {
                    updatePrefMutation.mutate({
                      workspace_id: currentWorkspace.id,
                      accounts: {
                        ...prefs?.data?.accounts,
                        metrics: {
                          grouping: bucket,
                          time_window: value,
                          items: [],
                        },
                      },
                      users: prefs?.data?.users,
                      updated_by: prefs?.data?.updated_by,
                      id: prefs?.data?.id,
                    });
                  }
                }}
              >
                <SelectTrigger className="w-[130px] h-7 text-xs">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {durationList.map((item) => (
                    <SelectItem key={item.value} value={item.value} className="text-xs">
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <div className="relative group">
            <button
              type="button"
              disabled={metrics?.length >= 8}
              onClick={() => setOpened(true)}
              className="inline-flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-200 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="h-3 w-3" />
              New Pinned Metric
            </button>
            {metrics?.length >= 8 && (
              <div className="absolute right-0 mt-1 w-60 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none text-center">
                Maximum pinned metrics reached. Remove some to add more.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="w-full">
        <div className="grid grid-cols-3 gap-4 min-h-[180px] relative">
          {loading && hasChartMetrics && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-lg">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-purple-600" />
            </div>
          )}
          {metrics?.length !== 0 ? (Object.keys(tableData)?.length !== 0
          && Object.entries(tableData).map(([key, data]: any) => {
            const selectedEvent = metrics?.find((event: any) => event.aggregator === key);
            return selectedEvent && (
              <div key={key}>
                <CustomChart
                  title={selectedEvent.display_name}
                  editTitleHandler={onEditTitle}
                  data={data}
                  type={selectedEvent.type}
                  deleteHandler={onDeleteClick}
                  id={selectedEvent.id}
                  updateMetricHandler={onUpdateMetric}
                  metricsList={fullMetricsList}
                  aggregator={selectedEvent.aggregator}
                  fieldDef={fieldDefMap[selectedEvent.aggregator] || fieldDefMap[selectedEvent.aggregator?.slice(3)]}
                />
              </div>
            );
          })) : (
            <div className="col-span-3 flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-gray-400" />
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">No metrics pinned</h4>
              <p className="text-xs text-gray-500 max-w-[280px]">
                Add your first custom success metric and generate graphs to view trends.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendsContainer;
